import { useI18nContext } from '@/i18n/i18n-react';
import type { OrdersDrilldownOrdersResponseDto } from '@/generated/korza-api-client/types.gen';
import type { ChartGranularity } from '@/lib/chart-axis';
import {
  DRILLDOWN_ORIGIN_CONFIG,
  getDrilldownBucketLabel,
  getDrilldownStatKinds,
  isRepurchaseFilterSegment,
  translateToken,
  type DrilldownOrigin,
  type DrilldownQuery,
  type DrilldownStatKind,
} from '@/lib/orders-drilldown';
import { formatCurrency } from '@/components/metrics/formatters';
import type { DrilldownStat } from '@/components/orders-drilldown/orders-drilldown-stats';

/** Same shape in the orders and the customers responses. */
type DrilldownSummary = OrdersDrilldownOrdersResponseDto['summary'];

interface UseOrdersDrilldownViewModelInput {
  query: DrilldownQuery | null;
  origin: DrilldownOrigin | null;
  granularity: ChartGranularity;
  /** Label of the filtered period, shown when the drill-down has no bucket. */
  periodLabel: string;
  summary: DrilldownSummary | undefined;
  /** Total rows of the active view; undefined while nothing has loaded. */
  total: number | undefined;
  isError: boolean;
}

interface OrdersDrilldownViewModel {
  title: string;
  /** Null while loading (skeletons); empty on error. */
  stats: DrilldownStat[] | null;
  emptyText: string;
  isEmpty: boolean;
  canExport: boolean;
  isRepurchase: boolean;
}

/** Title, summary strip and empty text shared by the dialog and the sheet. */
export function useOrdersDrilldownViewModel({
  query,
  origin,
  granularity,
  periodLabel,
  summary,
  total,
  isError,
}: UseOrdersDrilldownViewModelInput): OrdersDrilldownViewModel {
  const { LL, locale } = useI18nContext();
  const view = query?.view ?? 'orders';
  const search = query?.search ?? null;

  const bucketLabel = getDrilldownBucketLabel(
    query?.bucketStart ?? null,
    granularity,
    locale
  );
  // Repurchase segments are switched with the in-sheet filter, so the title
  // stays on the stable "Repurchases"; first purchases have no such filter.
  const isRepurchase =
    origin !== null && DRILLDOWN_ORIGIN_CONFIG[origin].isRepurchase;
  const titleOrigin =
    origin === null
      ? null
      : isRepurchaseFilterSegment(origin)
        ? 'total_repurchase'
        : origin;
  const title =
    titleOrigin === null
      ? ''
      : `${translateToken(LL, DRILLDOWN_ORIGIN_CONFIG[titleOrigin].titleToken)} · ${bucketLabel ?? periodLabel}`;

  const isEmpty = total === 0;
  const canExport = total !== undefined && total > 0;

  const stats: DrilldownStat[] | null = (() => {
    if (isError) return [];
    if (!summary) return null;
    const formatCount = (n: number) =>
      new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(n);
    const formatMoney = (n: number) =>
      formatCurrency(n, locale, summary.currency);
    const statByKind: Record<DrilldownStatKind, DrilldownStat> = {
      customers: {
        label: LL.metricsPage.repurchaseDrilldown.stats.customers(),
        value: summary.customerCount ?? total ?? 0,
        format: formatCount,
      },
      orders: {
        label: LL.metricsPage.repurchaseDrilldown.stats.orders(),
        value: summary.orderCount,
        format: formatCount,
      },
      revenue: {
        label: LL.metricsPage.repurchaseDrilldown.stats.revenue(),
        value: summary.revenue,
        format: formatMoney,
      },
      averageTicket: {
        label: LL.metricsPage.repurchaseDrilldown.stats.averageTicket(),
        value: summary.averageOrderValue,
        format: formatMoney,
      },
      // The backend sums `totalAmount` into `revenue` whatever the source.
      unrealizedAmount: {
        label: LL.metricsPage.repurchaseDrilldown.stats.unrealizedAmount(),
        value: summary.revenue,
        format: formatMoney,
      },
    };
    const amountKind =
      origin === null ? 'revenue' : DRILLDOWN_ORIGIN_CONFIG[origin].amountKind;
    return getDrilldownStatKinds(amountKind, view).map(
      (kind) => statByKind[kind]
    );
  })();

  const emptyText =
    search !== null
      ? LL.metricsPage.repurchaseDrilldown.searchEmpty({ search })
      : view === 'customers'
        ? LL.metricsPage.repurchaseDrilldown.customersEmpty()
        : LL.metricsPage.repurchaseDrilldown.empty();

  return { title, stats, emptyText, isEmpty, canExport, isRepurchase };
}
